import { isNumeric } from '../../util/helpers';

import { LatLon } from '../../util/geodesy/latlon';
import { Wind } from './wind.model';
import { SkyCondition } from './sky-condition.model';
import { FlightCategory } from './flight-category.model';
import { parseLatLon } from '../../airport/util/parseUtils';
import { convert, FEET, METERS } from '../../util/units';


/**
 * METAR type (routine vs special issuance)
 */
export type MetarType = 'METAR'|'SPECI';


/**
 * METAR model (aviation routine weather report)
 */
export class Metar {
  /**
   * Station identifier (typically the ICAO identifier of the corresponding airport)
   */
  stationId: string;

  /**
   * Station latitude and longitude
   */
  location: LatLon;

  /**
   * Station elevation in feet
   */
  elevation: number;

  /**
   * Observation time of the report
   */
  observationTime: Date;

  /**
   * METAR raw text
   */
  rawText: string;

  /**
   * Altimeter setting (in inches of mercury)
   */
  altimeter: number;

  /**
   * Ceiling (formatted as a string)
   */
  ceiling: string;

  /**
   * Visibility (in statute miles), returned as a formatted HTML string (possibly with alert/warn CSS class)
   */
  visibility: string;

  /**
   * Weather string (raw text)
   */
  wxString: string;

  /**
   * Weather string (translated), returned as a formatted HTML string (possible with alert/warn CSS classes)
   */
  wxStringTrans: string;

  /**
   * Wind
   */
  wind: Wind;

  /**
   * Sky condition array
   */
  skyConditions: Array<SkyCondition>;

  /**
   * Tempoerature (in degrees Celsius)
   */
  temperature: number;

  /**
   * Dew point (in degrees Celsius)
   */
  dewpoint: number;

  /**
   * Flight category
   */
  flightCategory: FlightCategory;

  /**
   * METAR report type
   */
  metarType: MetarType;

  /**
   * Parses a METAR from the API
   * @param data
   * @returns {Metar}
   */
  static parse(data: any): Metar {
    return {
      stationId: data.icao_code,
      location: parseLatLon(data.latitude, data.longitude),
      elevation: isNumeric(data.elevation_m) ? convert(data.elevation_m, METERS, FEET) : null,
      observationTime: data.observation_time ? new Date(data.observation_time) : null,
      rawText: data.raw_text,
      altimeter: isNumeric(data.altim_in_hg) ? +data.altim_in_hg : null,
      ceiling: data.ceiling,
      visibility: (data.visibility_statute_mi && data.visibility_statute_mi !== 'Unknown') ? data.visibility_statute_mi : null,
      wxString: data.wx_string || '',
      wxStringTrans: data.wx_string_trans || '',
      wind: Wind.parse(data.wind_dir_degrees, data.wind_speed_kt, data.wind_gust_kt),
      skyConditions: (data.sky_conditions_trans || []).map(skc => SkyCondition.parse(skc.cloud_base, skc.sky_cover)),
      temperature: isNumeric(data.temp_c) ? +data.temp_c : null,
      dewpoint: isNumeric(data.dewpoint_c) ? +data.dewpoint_c : null,
      flightCategory: data.flight_category || null,
      metarType: data.metar_type || null
    };
  }
}
